// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import FinanceDimensionValue from "./finance-dimension-value.js"

const modelClassData = {
  "attributes": {
    "code": {
      "column": {
        "default": null,
        "name": "code",
        "null": false,
        "type": "string"
      },
      "name": "code",
      "selected_by_default": null,
      "translated": false
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": null,
      "translated": true
    }
  },
  "className": "FinanceDimension",
  "collectionKey": "finance_dimensions",
  "collectionName": "finance_dimensions",
  "i18nKey": "finance_dimension",
  "camelizedLower": "financeDimension",
  "name": "FinanceDimension",
  "nameDasherized": "finance-dimension",
  "pluralName": "finance_dimensions",
  "ransackable_associations": [
    {
      "className": "FinanceDimension::Translation",
      "collectionName": null,
      "foreignKey": "finance_dimension_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "FinanceDimensionValue",
      "collectionName": "finance_dimension_values",
      "foreignKey": "finance_dimension_id",
      "name": "finance_dimension_values",
      "macro": "has_many",
      "resource_name": "FinanceDimensionValue",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "FinanceDimension::Translation",
      "collectionName": null,
      "foreignKey": "finance_dimension_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "code",
      "column": {
        "default": null,
        "name": "code",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "code_caption",
      "column": {
        "default": null,
        "name": "code_caption",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "FinanceDimensionValue",
      "collectionName": "finance_dimension_values",
      "foreignKey": "finance_dimension_id",
      "name": "finance_dimension_values",
      "macro": "has_many",
      "resource_name": "FinanceDimensionValue",
      "through": null
    }
  ],
  "paramKey": "finance_dimension",
  "primaryKey": "id"
}

/** Frontend model for FinanceDimension. */
export default class FinanceDimension extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  code() {
    return this.readAttributeUnderscore("code")
  }

  /** @returns {boolean} */
  hasCode() {
    const value = this.code()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static sync(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "sync",
        collectionName: "finance_dimensions",
        type: "collection"
      },
      commandArgs
    ))
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./finance-dimension-value.js").default>} */
  financeDimensionValues() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["finance_dimension_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "finance_dimension_values",
        model: this,
        modelClass: FinanceDimensionValue
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./finance-dimension-value.js").default>>} */
  loadFinanceDimensionValues() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["finance_dimension_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "finance_dimension_values",
        model: this,
        modelClass: FinanceDimensionValue
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./finance-dimension-value.js").default>>} */
  ensureFinanceDimensionValuesLoaded() {
    return this.ensureAssociationLoaded("finance_dimension_values")
  }
}
